import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAdminIo } from '../auth/useAdmin'
import { useImpersonation } from '../auth/useImpersonation'
import { copyVersionToFeatured, deleteToy, FEATURED_IDENTITY_ID } from '../upload/adminS3'
import { loadProfile, type Profile } from '../upload/profile'
import {
  listUserToys,
  loadToyIndexWithMeta,
  regenerateToyIndex,
  saveToyIndexOrder,
  type LoadedToyIndex,
  type ToyIndex,
} from '../upload/toyIndex'
import { listToyVersionSummaries } from '../upload/toyInspection'
import { fetchFeaturedToys } from '../lib/featuredToys'
import {
  filterNewerBuilds,
  firmwareMetaOf,
  listFirmwareBuilds,
  type FirmwareBuild,
} from '../upload/fetchFirmware'
import { isPreconditionFailedError, type S3Io } from '../upload/s3'
import { str } from '../types/toyEntry'
import ToyIndexOrder from '../components/ToyIndexOrder'
import './Admin.css'

type ToyRow = {
  id: string
  title?: string
  creatorName?: string
  description?: string
  onDisk: boolean
  inIndex: boolean
}

// Result of scanning one toy for pending firmware/version action.
type FirmwareScan =
  | { kind: 'ok'; newerFirmware: boolean; stagedVersion: boolean }
  | { kind: 'error'; message: string }

const SCAN_CONCURRENCY = 4

// Run a per-toy scan over `toyIds` with a bounded worker pool, streaming each
// result into `setResults` as it completes. Shared by the firmware scan and the
// Featured toy-update scan, which differ only in the per-toy scan function.
async function runToyScan<T>(
  toyIds: string[],
  scanOne: (toyId: string) => Promise<T>,
  onError: (toyId: string, err: unknown) => T,
  setResults: Dispatch<SetStateAction<Map<string, T>>>,
): Promise<void> {
  let cursor = 0
  const worker = async () => {
    while (cursor < toyIds.length) {
      const toyId = toyIds[cursor++]
      let result: T
      try {
        result = await scanOne(toyId)
      } catch (err) {
        result = onError(toyId, err)
      }
      setResults((prev) => new Map(prev).set(toyId, result))
    }
  }
  await Promise.all(Array.from({ length: Math.min(SCAN_CONCURRENCY, toyIds.length) }, worker))
}

// Scan a single toy: is a firmware build newer than its newest version's
// firmware available, and is its newest version folder unpublished (staged)?
// `buildsCache` dedupes the per-package binary listing across toys.
async function scanToyFirmware(
  io: S3Io,
  identityId: string,
  toyId: string,
  buildsCache: Map<string, FirmwareBuild[]>,
): Promise<FirmwareScan> {
  const summaries = await listToyVersionSummaries(io, identityId, toyId)
  if (summaries.length === 0) return { kind: 'ok', newerFirmware: false, stagedVersion: false }
  // Sorted newest-first: the top folder is staged when it isn't the live one.
  const stagedVersion = !summaries[0].isLatest
  const { packageName, buildTime } = firmwareMetaOf(summaries[0].metadata)
  let newerFirmware = false
  if (packageName) {
    let builds = buildsCache.get(packageName)
    if (!builds) {
      builds = await listFirmwareBuilds(io, packageName)
      buildsCache.set(packageName, builds)
    }
    newerFirmware = filterNewerBuilds(builds, buildTime).length > 0
  }
  return { kind: 'ok', newerFirmware, stagedVersion }
}

// Result of comparing one Featured toy against its original source folder.
type ToyUpdateScan =
  | {
      kind: 'ok'
      sourceIdentityId: string
      liveMatches: boolean
      featuredLive: string | null
      sourceLive: string | null
      uncopiedFromSource: string[]
    }
  | { kind: 'no-source'; message: string }
  | { kind: 'error'; message: string }

// Compare a Featured toy against its original creator's folder. The source
// creator is read from the copied metadata (copyVersionToFeatured preserves
// creatorId), then version sets and live pointers are compared to surface
// drift: versions the source has that were never copied, and a live pointer
// that no longer matches the source.
async function scanToyDivergence(io: S3Io, toyId: string): Promise<ToyUpdateScan> {
  const featured = await listToyVersionSummaries(io, FEATURED_IDENTITY_ID, toyId)
  if (featured.length === 0) return { kind: 'no-source', message: 'no versions in Featured folder' }
  const creatorId = str(featured[0].metadata?.creatorId)
  if (!creatorId || creatorId === FEATURED_IDENTITY_ID) {
    return { kind: 'no-source', message: 'no source creator recorded' }
  }
  const source = await listToyVersionSummaries(io, creatorId, toyId)
  if (source.length === 0) return { kind: 'no-source', message: 'source folder empty or removed' }
  const featuredLive = featured.find((s) => s.isLatest)?.version ?? null
  const sourceLive = source.find((s) => s.isLatest)?.version ?? null
  const featuredVersions = new Set(featured.map((s) => s.version))
  // Newest-first order is preserved from listToyVersionSummaries.
  const uncopiedFromSource = source.map((s) => s.version).filter((v) => !featuredVersions.has(v))
  return {
    kind: 'ok',
    sourceIdentityId: creatorId,
    liveMatches: featuredLive === sourceLive,
    featuredLive,
    sourceLive,
    uncopiedFromSource,
  }
}

// Object keys sorted recursively so entry/key ordering doesn't produce false
// mismatches when comparing two toy-index entries.
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  if (value && typeof value === 'object') {
    const body = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
      .join(',')
    return `{${body}}`
  }
  return JSON.stringify(value) ?? 'null'
}

type IndexDiff = {
  matches: boolean
  changed: string[]
  onlyInS3: string[]
  onlyInCommitted: string[]
  orderDiffers: boolean
}

// Compare the committed toy-index.json against the S3 copy, keyed by toy id.
// Array order is the public display order, so a pure reorder counts as drift
// too: the shared-id sequences are compared (ignoring adds/removes, which are
// already reported separately).
function diffIndexes(committed: unknown[], s3: ToyIndex): IndexDiff {
  const keyById = (list: unknown[]) => {
    const map = new Map<string, unknown>()
    for (const entry of list) {
      const id = entry && typeof entry === 'object' ? (entry as Record<string, unknown>).id : null
      if (typeof id === 'string') map.set(id, entry)
    }
    return map
  }
  const committedMap = keyById(committed)
  const s3Map = keyById(s3)
  const changed: string[] = []
  const onlyInS3: string[] = []
  const onlyInCommitted: string[] = []
  for (const [id, entry] of s3Map) {
    const other = committedMap.get(id)
    if (other === undefined) onlyInS3.push(id)
    else if (stableStringify(entry) !== stableStringify(other)) changed.push(id)
  }
  for (const id of committedMap.keys()) {
    if (!s3Map.has(id)) onlyInCommitted.push(id)
  }
  const sharedSequence = (map: Map<string, unknown>, other: Map<string, unknown>) =>
    [...map.keys()].filter((id) => other.has(id)).join('\n')
  const orderDiffers =
    sharedSequence(s3Map, committedMap) !== sharedSequence(committedMap, s3Map)
  return {
    matches:
      changed.length === 0 &&
      onlyInS3.length === 0 &&
      onlyInCommitted.length === 0 &&
      !orderDiffers,
    changed: changed.sort(),
    onlyInS3: onlyInS3.sort(),
    onlyInCommitted: onlyInCommitted.sort(),
    orderDiffers,
  }
}

type IndexSync = { kind: 'ok'; diff: IndexDiff } | { kind: 'error'; message: string }

// Fetch the committed/deployed toy-index.json and diff it against S3.
// `cache: 'no-store'` so a stale HTTP cache doesn't mask real drift.
async function checkIndexSync(s3Index: ToyIndex): Promise<IndexSync> {
  try {
    const committed = await fetchFeaturedToys({ cache: 'no-store' })
    return { kind: 'ok', diff: diffIndexes(committed, s3Index) }
  } catch (err) {
    return { kind: 'error', message: err instanceof Error ? err.message : 'compare failed' }
  }
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: Profile | null; rows: ToyRow[]; indexMeta: LoadedToyIndex }
  | { kind: 'error'; message: string }

function buildRows(index: ToyIndex, diskToyIds: string[]): ToyRow[] {
  const diskSet = new Set(diskToyIds)
  const indexMap = new Map<string, Record<string, unknown>>()
  for (const e of index) {
    const id = typeof e.id === 'string' ? e.id : null
    if (id) indexMap.set(id, e)
  }
  const ids = new Set<string>([...diskSet, ...indexMap.keys()])
  const rows: ToyRow[] = []
  for (const id of ids) {
    const entry = indexMap.get(id)
    rows.push({
      id,
      title: entry ? str(entry.title) : undefined,
      creatorName: entry ? str(entry.creatorName) : undefined,
      description: entry ? str(entry.description) : undefined,
      onDisk: diskSet.has(id),
      inIndex: !!entry,
    })
  }
  rows.sort((a, b) => a.id.localeCompare(b.id))
  return rows
}

function FirmwareStatus({ scan, scanning }: { scan: FirmwareScan | undefined; scanning: boolean }) {
  if (!scan) {
    return scanning ? <div className="admin-user__status-note">scanning firmware…</div> : null
  }
  if (scan.kind === 'error') {
    return <div className="admin-user__status-note">firmware scan failed: {scan.message}</div>
  }
  if (!scan.newerFirmware && !scan.stagedVersion) {
    return <div className="admin-user__status-note">up to date</div>
  }
  return (
    <>
      {scan.newerFirmware && (
        <div className="admin-user__fw-alert">new firmware binary available</div>
      )}
      {scan.stagedVersion && (
        <div className="admin-user__fw-alert admin-user__fw-alert--staged">
          new version not live
        </div>
      )}
    </>
  )
}

function IndexSyncStatus({ sync }: { sync: IndexSync | null }) {
  if (!sync) {
    return <div className="admin-user__status-note">checking committed toy-index.json…</div>
  }
  if (sync.kind === 'error') {
    return <div className="admin-user__status-note">index compare failed: {sync.message}</div>
  }
  const { diff } = sync
  if (diff.matches) {
    return <div className="admin-user__status-note">✓ Committed toy-index.json matches S3</div>
  }
  const parts: string[] = []
  if (diff.changed.length) parts.push(`changed: ${diff.changed.join(', ')}`)
  if (diff.onlyInS3.length) parts.push(`only in S3: ${diff.onlyInS3.join(', ')}`)
  if (diff.onlyInCommitted.length) parts.push(`only in commit: ${diff.onlyInCommitted.join(', ')}`)
  if (diff.orderDiffers) parts.push('entry order differs')
  return (
    <div className="admin-user__fw-alert">
      Committed toy-index.json is out of sync with S3 ({parts.join('; ')}). Run get_toy_index.sh,
      commit, and redeploy.
    </div>
  )
}

function ToyUpdateStatus({
  scan,
  scanning,
}: {
  scan: ToyUpdateScan | undefined
  scanning: boolean
}) {
  if (!scan) {
    return scanning ? <div className="admin-user__status-note">scanning source…</div> : null
  }
  if (scan.kind === 'error') {
    return <div className="admin-user__status-note">toy scan failed: {scan.message}</div>
  }
  if (scan.kind === 'no-source') {
    return <div className="admin-user__status-note">{scan.message}</div>
  }
  if (scan.liveMatches && scan.uncopiedFromSource.length === 0) {
    return <div className="admin-user__status-note">in sync with source</div>
  }
  return (
    <>
      {!scan.liveMatches && (
        <div className="admin-user__fw-alert">
          live differs — Featured {scan.featuredLive ?? 'none'} vs source{' '}
          {scan.sourceLive ?? 'none'}
        </div>
      )}
      {scan.uncopiedFromSource.length > 0 && (
        <div className="admin-user__fw-alert">
          source has uncopied versions: {scan.uncopiedFromSource.join(', ')}
        </div>
      )}
    </>
  )
}

export default function AdminUser() {
  const { identityId: rawIdentityId } = useParams<{ identityId: string }>()
  const identityId = rawIdentityId ? decodeURIComponent(rawIdentityId) : ''
  const io = useAdminIo()
  const navigate = useNavigate()
  const { impersonate } = useImpersonation()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [busy, setBusy] = useState<string | null>(null)
  const [fwScanning, setFwScanning] = useState(false)
  const [fwResults, setFwResults] = useState<Map<string, FirmwareScan>>(new Map())
  const [tuScanning, setTuScanning] = useState(false)
  const [tuResults, setTuResults] = useState<Map<string, ToyUpdateScan>>(new Map())
  const [indexSync, setIndexSync] = useState<IndexSync | null>(null)

  const isFeaturedFolder = identityId === FEATURED_IDENTITY_ID

  const load = useCallback(async () => {
    if (!io || !identityId) return
    setState({ kind: 'loading' })
    setFwResults(new Map())
    setTuResults(new Map())
    setIndexSync(null)
    try {
      const [profile, indexMeta, diskToys] = await Promise.all([
        loadProfile(io, identityId),
        loadToyIndexWithMeta(io, identityId),
        listUserToys(io, identityId),
      ])
      setState({ kind: 'ready', profile, rows: buildRows(indexMeta.index, diskToys), indexMeta })
      if (isFeaturedFolder) setIndexSync(await checkIndexSync(indexMeta.index))
    } catch (err) {
      console.error('[admin] load user view failed', { identityId }, err)
      const message = err instanceof Error ? err.message : 'Failed to load'
      setState({ kind: 'error', message })
    }
  }, [io, identityId, isFeaturedFolder])

  useEffect(() => {
    void load()
  }, [load])

  const handleDelete = async (toyId: string) => {
    if (!io) return
    if (!window.confirm(`Delete toy ${toyId}? This removes all its objects and its index entry.`)) {
      return
    }
    setBusy(`delete:${toyId}`)
    try {
      await deleteToy(io, identityId, toyId)
      await load()
    } catch (err) {
      console.error('[admin] deleteToy failed', { identityId, toyId }, err)
      const message = err instanceof Error ? err.message : 'Delete failed'
      window.alert(`Delete failed: ${message}`)
    } finally {
      setBusy(null)
    }
  }

  const handleCopyLatest = async (
    toyId: string,
    scan: Extract<ToyUpdateScan, { kind: 'ok' }>,
  ) => {
    if (!io) return
    const version = scan.uncopiedFromSource[0]
    if (
      !window.confirm(
        `Copy version ${version} of toy ${toyId} from its source creator to Featured Apps?\n\n` +
          'It keeps its version number and lands dormant — promote it from the featured ' +
          "toy's versions page when ready. (The first featured version of a toy goes live " +
          'immediately.)',
      )
    ) {
      return
    }
    setBusy(`copy:${toyId}`)
    try {
      const { live } = await copyVersionToFeatured(io, scan.sourceIdentityId, toyId, version)
      window.alert(
        live
          ? `Copied ${version} to Featured and it is now LIVE (first featured version). ` +
              'Pull the updated data/toy-index.json and redeploy to publish it.'
          : `Copied ${version} to Featured as a dormant version. Promote it from the ` +
              "featured toy's versions page when ready.",
      )
      // Refresh only this row's divergence status — load() would wipe every
      // scan result on the page.
      try {
        const fresh = await scanToyDivergence(io, toyId)
        setTuResults((prev) => new Map(prev).set(toyId, fresh))
      } catch (err) {
        console.error('[admin] toy update rescan failed', { toyId }, err)
        setTuResults((prev) =>
          new Map(prev).set(toyId, {
            kind: 'error',
            message: err instanceof Error ? err.message : 'rescan failed',
          }),
        )
      }
    } catch (err) {
      console.error('[admin] copyVersionToFeatured failed', { toyId, version }, err)
      const message = err instanceof Error ? err.message : 'Copy failed'
      window.alert(`Copy failed: ${message}`)
    } finally {
      setBusy(null)
    }
  }

  const handleRegenerate = async () => {
    if (!io) return
    if (
      !window.confirm(
        'Rebuild toy-index.json from metadata.json files on disk? Any entry whose metadata.json has not yet been uploaded will be dropped.',
      )
    ) {
      return
    }
    setBusy('regenerate')
    try {
      const { entryCount } = await regenerateToyIndex(io, identityId)
      window.alert(`Rebuilt index with ${entryCount} entries.`)
      await load()
    } catch (err) {
      console.error('[admin] regenerateToyIndex failed', { identityId }, err)
      const message = err instanceof Error ? err.message : 'Regenerate failed'
      window.alert(`Regenerate failed: ${message}`)
    } finally {
      setBusy(null)
    }
  }

  const handleSaveOrder = async (reordered: ToyIndex) => {
    if (!io || state.kind !== 'ready') return
    setBusy('save-order')
    try {
      await saveToyIndexOrder(io, identityId, reordered, state.indexMeta.etag)
      window.alert(
        'Saved new order.' +
          (isFeaturedFolder ? ' Run get_toy_index.sh, commit, and redeploy to publish.' : ''),
      )
      await load()
    } catch (err) {
      if (isPreconditionFailedError(err)) {
        window.alert(
          'toy-index.json changed in S3 since it was loaded — reloading. Please redo the reorder.',
        )
        await load()
      } else {
        console.error('[admin] saveToyIndexOrder failed', { identityId }, err)
        const message = err instanceof Error ? err.message : 'Save failed'
        window.alert(`Save failed: ${message}`)
      }
    } finally {
      setBusy(null)
    }
  }

  const handleScanFirmware = async () => {
    if (!io || state.kind !== 'ready') return
    const toyIds = state.rows.filter((r) => r.onDisk).map((r) => r.id)
    setFwResults(new Map())
    setFwScanning(true)
    const buildsCache = new Map<string, FirmwareBuild[]>()
    try {
      await runToyScan<FirmwareScan>(
        toyIds,
        (toyId) => scanToyFirmware(io, identityId, toyId, buildsCache),
        (toyId, err) => {
          console.error('[admin] firmware scan failed', { identityId, toyId }, err)
          return { kind: 'error', message: err instanceof Error ? err.message : 'scan failed' }
        },
        setFwResults,
      )
    } finally {
      setFwScanning(false)
    }
  }

  const handleScanToyUpdates = async () => {
    if (!io || state.kind !== 'ready') return
    const toyIds = state.rows.filter((r) => r.onDisk).map((r) => r.id)
    setTuResults(new Map())
    setTuScanning(true)
    try {
      await runToyScan<ToyUpdateScan>(
        toyIds,
        (toyId) => scanToyDivergence(io, toyId),
        (toyId, err) => {
          console.error('[admin] toy update scan failed', { identityId, toyId }, err)
          return { kind: 'error', message: err instanceof Error ? err.message : 'scan failed' }
        },
        setTuResults,
      )
    } finally {
      setTuScanning(false)
    }
  }

  const handleImpersonate = () => {
    const name = state.kind === 'ready' ? state.profile?.creatorName : undefined
    impersonate(identityId, name ?? undefined)
    navigate('/')
  }

  if (!identityId) {
    return <p>Missing user id.</p>
  }

  return (
    <section className="admin">
      <Link to="/admin" className="detail-back">
        ← All users
      </Link>

      <header className="admin-user__header">
        <h1 className="page-title">
          {state.kind === 'ready' && state.profile?.creatorName
            ? state.profile.creatorName
            : 'User'}
        </h1>
        <p className="admin-user__id">{identityId}</p>
        {isFeaturedFolder && <IndexSyncStatus sync={indexSync} />}
      </header>

      <div className="admin-user__actions">
        <button type="button" onClick={handleRegenerate} disabled={busy !== null}>
          {busy === 'regenerate' ? 'Regenerating…' : 'Regenerate toy-index.json'}
        </button>
        {isFeaturedFolder ? (
          <button
            type="button"
            onClick={handleScanToyUpdates}
            disabled={busy !== null || tuScanning || state.kind !== 'ready'}
          >
            {tuScanning ? 'Scanning toys…' : 'Scan for toy updates'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleScanFirmware}
            disabled={busy !== null || fwScanning || state.kind !== 'ready'}
          >
            {fwScanning ? 'Scanning firmware…' : 'Scan for firmware updates'}
          </button>
        )}
        {!isFeaturedFolder && (
          <button type="button" onClick={handleImpersonate} disabled={busy !== null}>
            Impersonate this user
          </button>
        )}
      </div>

      {state.kind === 'ready' && (
        <ToyIndexOrder
          key={state.indexMeta.etag ?? 'none'}
          entries={state.indexMeta.index}
          showPublishHint={isFeaturedFolder}
          disabled={busy !== null}
          saving={busy === 'save-order'}
          onSave={handleSaveOrder}
        />
      )}

      {state.kind === 'loading' && <p>Loading…</p>}
      {state.kind === 'error' && (
        <p className="admin__error" role="alert">
          {state.message}
        </p>
      )}
      {state.kind === 'ready' && state.rows.length === 0 && <p>No toys found.</p>}
      {state.kind === 'ready' && state.rows.length > 0 && (
        <table className="admin-user__table">
          <thead>
            <tr>
              <th>Toy</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {state.rows.map((row) => {
              const status = row.onDisk && row.inIndex
                ? { label: 'indexed', cls: 'indexed' as const }
                : row.onDisk
                  ? { label: 'on disk, not in index', cls: 'orphan' as const }
                  : { label: 'in index, missing on disk', cls: 'missing' as const }
              const scan = isFeaturedFolder ? tuResults.get(row.id) : undefined
              return (
                <tr key={row.id}>
                  <td>
                    <div>{row.title ?? <em>(no title)</em>}</div>
                    {row.creatorName && <div>{row.creatorName}</div>}
                    {row.description && (
                      <div className="admin-user__toy-description">{row.description}</div>
                    )}
                    <div className="admin-user__toy-id">{row.id}</div>
                  </td>
                  <td className={`admin-user__status--${status.cls}`}>
                    <div>{status.label}</div>
                    {isFeaturedFolder ? (
                      <ToyUpdateStatus
                        scan={scan}
                        scanning={tuScanning && row.onDisk}
                      />
                    ) : (
                      <FirmwareStatus
                        scan={fwResults.get(row.id)}
                        scanning={fwScanning && row.onDisk}
                      />
                    )}
                  </td>
                  <td className="admin-user__row-actions">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/admin/users/${encodeURIComponent(identityId)}/toys/${row.id}`,
                        )
                      }
                      disabled={busy !== null}
                    >
                      Details
                    </button>
                    {isFeaturedFolder &&
                      scan?.kind === 'ok' &&
                      scan.uncopiedFromSource.length > 0 && (
                        <button
                          type="button"
                          onClick={() => void handleCopyLatest(row.id, scan)}
                          disabled={busy !== null}
                        >
                          {busy === `copy:${row.id}` ? 'Copying…' : 'Copy latest version'}
                        </button>
                      )}
                    <button
                      type="button"
                      onClick={() => handleDelete(row.id)}
                      disabled={busy !== null}
                    >
                      {busy === `delete:${row.id}` ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}
